import { PowerBIEmbed } from "powerbi-client-react";
import { Embed, Page, Report, models } from "powerbi-client";
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "utils/hooks/storeHooks";
import { selectEmbedToken } from "store/session/selectors";
import { getEmbedToken } from "store/session/thunks";
import { useDispatch } from "react-redux";
import styles from "./PowerBIReport.module.scss";

type PowerBIReportParams = {
  page: number;
  values: any;
  setIsReportLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setReportRef: React.Dispatch<React.SetStateAction<Report | undefined>>;
  setReportState: React.Dispatch<React.SetStateAction<string>>;
};

const PowerBIReport = ({
  page,
  values,
  setIsReportLoaded,
  setReportRef,
  setReportState,
}: PowerBIReportParams) => {
  const dispatch = useDispatch();
  const [report, setReport] = useState<Report>();
  const [pages, setPages] = useState<Page[]>();
  const embedToken = useAppSelector(selectEmbedToken);
  const embedUrl = `https://app.powerbi.com/reportEmbed?reportId=${values?.reportId}&groupId=d0aa1a69-69a2-4202-af6b-22af36ebdb1b&w=2&config=eyJjbHVzdGVyVXJsIjoiaHR0cHM6Ly9XQUJJLVVTLUNFTlRSQUwtQS1QUklNQVJZLXJlZGlyZWN0LmFuYWx5c2lzLndpbmRvd3MubmV0IiwiZW1iZWRGZWF0dXJlcyI6eyJtb2Rlcm5FbWJlZCI6dHJ1ZSwidXNhZ2VNZXRyaWNzVk5leHQiOnRydWV9fQ%3d%3d`;
  const filterArr: any[] = [];
  const [keyReport, setKeyReport] = useState(0);
  const ref = useRef({
    report: report,
  });

  useEffect(() => {
    if (report) {
      ref.current.report = report;
      setReportRef(report);
    }
  }, [report, setReportRef]);

  const getPageNames = async () => {
    // Gets the state of the saved filter and applies it to restore the graph visuals
    if (values?.templateState)
      await ref.current.report?.bookmarksManager.applyState(
        values?.templateState
      );
    setPages(await ref.current.report?.getPages());
  };

  const captureReportState = async () => {
    const clickedReport = ref.current.report;
    if (clickedReport) {
      const capturedState = await clickedReport.bookmarksManager.capture();
      if (capturedState.state) setReportState(capturedState.state);
    } else {
      console.log("No report state available");
    }
  };

  enum TablesEnum {
    taxonomy_table = 4,
    physician_table = 1,
  }
  enum ColumnCardEnum {
    enumeration_type = 1,
  }
  enum ColumnCollectionEnum {
    taxonomy_classification = 4,
  }

  function getTableById(id: number): string | null {
    const enumVal = TablesEnum[id];
    if (enumVal !== undefined) {
      return enumVal;
    } else {
      return null;
    }
  }
  function getColumnCardById(id: number): string | null {
    const enumVal = ColumnCardEnum[id];
    if (enumVal !== undefined) {
      return enumVal;
    } else {
      return null;
    }
  }
  function getColumnCollectionById(id: number): string | null {
    const enumVal = ColumnCollectionEnum[id];
    if (enumVal !== undefined) {
      return enumVal;
    } else {
      return null;
    }
  }

  if (values?.checkboxes && values?.checkboxes.length > 0) {
    filterArr.push({
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: String(getTableById(values?.providerId)),
        column: String(getColumnCollectionById(values?.providerId)),
      },
      operator: "In",
      values: values?.checkboxes || "",
      filterType: models.FilterType.Basic,
    });
  }
  // managing in powerbireport
  /*   if (values?.checkboxes.length > 0) {
    filterArr.push({
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: String("nucc_taxonomy_230.Taxonomy_Classification"),
        column: String(getColumnCollectionById(values?.providerId)),
      },
      operator: "In",
      values: values?.checkboxes || "",
      filterType: models.FilterType.Basic,
    });
  } */
  if (values?.cardValue) {
    filterArr.push({
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: String(getTableById(values?.providerId)),
        column: String(getColumnCardById(values?.providerId)),
      },
      operator: "In",
      values: [values?.cardValue] || "",
      filterType: models.FilterType.Basic,
    });
  }
  if (values?.cardValue) {
    filterArr.push({
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: String("Address_and_taxonomy"),
        column: String("npess_basic.enumeration_type"),
      },
      operator: "In",
      values: [values?.cardValue] || "",
      filterType: models.FilterType.Basic,
    });
  }

  if (values.genieFilters) {
    values.genieFilters.map((filters: any) => {
      if (filters.columnName === "postal_code") {
        filterArr.push({
          $schema: "http://powerbi.com/product/schema#advanced",
          target: {
            table: "physician_table",
            column: "postal_code",
          },
          logicalOperator: "And",
          conditions: [
            {
              operator: "Contains",
              value: filters.values[0] || "",
            },
          ],
          filterType: models.FilterType.Advanced,
        });
      }
      if (filters.columnName === "language_description") {
        filterArr.push({
          $schema: "http://powerbi.com/product/schema#advanced",
          target: {
            table: "physician_table",
            column: "language_description",
          },
          logicalOperator: "And",
          conditions: [
            {
              operator: "Contains",
              value: filters.values[0] || "",
            },
          ],
          filterType: models.FilterType.Advanced,
        });
      }
      if (
        filters.columnName !== "postal_code" &&
        filters.columnName !== "language_description"
      ) {
        filterArr.push({
          $schema: "http://powerbi.com/product/schema#basic",
          target: {
            table: filters.tableName,
            column: filters.columnName,
          },
          operator: "In",
          values: filters.values || "",
          filterType: models.FilterType.Basic,
        });
      }
    });
  }
  console.log(filterArr);

  const dispatchEmbedToken = () => {
    dispatch(getEmbedToken(String(values.providerId)));
  };

  useEffect(dispatchEmbedToken, [dispatch, values.providerId]);
  const embedConfig = {
    tokenType: models.TokenType.Embed,
    pageName: pages ? pages[page].name : "ReportSection",
    type: "report",
    id: values?.reportId,
    embedUrl,
    accessToken: embedToken,
    settings: {
      navContentPaneEnabled: false,
      filterPaneEnabled: false,
      bookmarksPaneEnabled: false,
      layoutType: models.LayoutType.Custom,
      customLayout: {
        pageSize: {
          type: models.PageSizeType.Widescreen,
        },
        displayOption: models.DisplayOption.FitToWidth,
      },
    },
    filters: filterArr,
  };
  return (
    <PowerBIEmbed
      key={keyReport}
      cssClassName={styles.embed}
      embedConfig={embedConfig}
      eventHandlers={
        new Map([
          [
            "loaded",
            function () {
              console.log("Report loaded");
              getPageNames();
            },
          ],
          [
            "rendered",
            function () {
              console.log("Report rendered");
              if (ref.current.report)
                setTimeout(() => {
                  setIsReportLoaded(true);
                }, 0);
              captureReportState();
            },
          ],
          [
            "error",
            function (event) {
              console.log(event && event.detail);
              if (event && event.detail.message === "LoadReportFailed") {
                console.log("restarting");

                setKeyReport(keyReport + 1);
              }
            },
          ],
          [
            "visualClicked",
            function () {
              console.log("Visual clicked");
              captureReportState();
            },
          ],
          ["pageChanged", (event) => console.log(event)],
        ])
      }
      getEmbeddedComponent={(embeddedReport: Embed) => {
        setReport(embeddedReport as Report);
      }}
    />
  );
};

export default PowerBIReport;
